import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Copy, Columns2, Loader2, ClipboardPaste, CopyCheck } from 'lucide-react';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatLocalDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const safe = dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr;
  const d = new Date(safe);
  return d.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' });
};

export const ReferenceNotePanel = ({ 
  selectedNote, 
  notes, 
  users, 
  formTemplates, 
  clientDiagnoses,
  locations,
  onClose,
  onApplyField,
  onApplyAll, // callback: (fields) => void — applies all fields at once
}) => {
  const [referenceNoteData, setReferenceNoteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [allClientNotes, setAllClientNotes] = useState(null);
  const [loadingAllNotes, setLoadingAllNotes] = useState(false);

  const recentNotes = notes
    .filter(n => {
      const sameClient = selectedNote?.is_group_note
        ? (n.client_ids || []).some(cid => (selectedNote?.client_ids || []).includes(cid))
        : n.client_id === selectedNote?.client_id;
      return sameClient && n.id !== selectedNote?.id;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 15);

  useEffect(() => {
    if (showAllNotes && !allClientNotes) {
      setLoadingAllNotes(true);
      const clientId = selectedNote?.client_id;
      if (!clientId) { setLoadingAllNotes(false); return; }
      axios.get(`${API}/notes?client_id=${clientId}&start_date=2020-01-01&summary=true&limit=200`, { headers: getAuthHeaders() })
        .then(resp => {
          const filtered = (resp.data || [])
            .filter(n => n.id !== selectedNote?.id)
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setAllClientNotes(filtered);
        })
        .catch(() => toast.error('Failed to load all notes'))
        .finally(() => setLoadingAllNotes(false));
    }
  }, [showAllNotes, allClientNotes, selectedNote]);

  const displayNotes = showAllNotes ? (allClientNotes || recentNotes) : recentNotes;

  const loadNote = async (noteId) => {
    if (!noteId) return;
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/notes/${noteId}`, { headers: getAuthHeaders() });
      setReferenceNoteData(resp.data);
    } catch (err) {
      console.error('Failed to load reference note:', err);
      toast.error('Failed to load reference note');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`Copied "${label}"`))
      .catch(() => toast.error('Failed to copy'));
  };

  const applyField = (fieldId, value, label) => {
    if (onApplyField) {
      onApplyField(fieldId, value, label);
      toast.success(`Applied "${label}" to current note`);
    } else {
      const text = Array.isArray(value) ? value.join(', ') : String(value || '');
      copyToClipboard(text, label);
    }
  };

  // Reusable action buttons for each field (Copy + Apply)
  const FieldActions = ({ fieldId, value, label, displayText }) => {
    const text = displayText || (Array.isArray(value) ? value.join(', ') : String(value || ''));
    return (
      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
        <button
          className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
          title={`Copy "${label}" to clipboard`}
          onClick={() => copyToClipboard(text, label)}
        >
          <Copy className="h-3 w-3" />
        </button>
        {onApplyField && (
          <button
            className="p-1 rounded hover:bg-teal-100 text-teal-500 hover:text-teal-700"
            title={`Apply "${label}" to current note`}
            onClick={() => applyField(fieldId, value, label)}
          >
            <ClipboardPaste className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    );
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'object') return option.label || option.value || String(option);
    return String(option);
  };

  // Resolve diagnosis IDs to readable text
  const resolveDiagnosisValue = (value) => {
    if (!Array.isArray(value)) return null;
    const resolved = value.map(item => {
      // item could be a UUID string or a full diagnosis object
      if (typeof item === 'object' && item !== null) {
        return `${item.code || item.diagnosis_code || ''} — ${item.description || item.diagnosis_description || 'Unknown'}`.trim();
      }
      // String ID — look up in clientDiagnoses
      if (clientDiagnoses?.length) {
        const dx = clientDiagnoses.find(d => d.id === item);
        if (dx) return `${dx.code || dx.diagnosis_code || ''} — ${dx.description || dx.diagnosis_description || ''}`.trim();
      }
      return String(item);
    });
    return resolved;
  };

  // Non-applyable field types (structural or complex)
  const isStructuralField = (ftype) => {
    return ['section_header', 'heading', 'matrix', 'signature', 'consent_affirmation', 'treatment_plan_goals'].includes(ftype);
  };

  const renderField = (field, idx) => {
    const label = field.label || field.name || '';
    const ftype = field.type || '';
    const value = field.value || field.default_value || '';
    const fieldId = field.id || '';

    if (!label || !label.trim()) {
      if (ftype === 'section_header') {
        return <div key={idx} className="border-b-2 mt-4 mb-2" style={{ borderColor: '#0d9488' }} />;
      }
      return null;
    }

    if (ftype === 'section_header' || ftype === 'heading') {
      const sectionColor = field.backgroundColor || '#0d9488';
      return (
        <div key={idx} className="w-full mt-4 mb-2">
          <div className="text-white px-3 py-1.5 rounded-md shadow-sm text-xs font-semibold tracking-wide" style={{ backgroundColor: sectionColor }}>
            {label}
          </div>
        </div>
      );
    }

    // Consent affirmation — pull from note-level telehealth_consent
    if (ftype === 'consent_affirmation') {
      const consent = referenceNoteData?.telehealth_consent || (typeof value === 'object' && value !== null ? value : null);
      const hasConsentData = consent && typeof consent === 'object' && 
        Object.values(consent).some(v => v !== null && v !== undefined && v !== '');
      if (hasConsentData) {
        const parts = [];
        if (consent.provider_location) {
          const locName = consent.provider_location === 'home' 
            ? "Provider's Home" 
            : (locations || []).find(l => l.id === consent.provider_location)?.name || consent.provider_location;
          parts.push(`Location: ${locName}`);
        }
        if (consent.pos_code) parts.push(`POS: ${consent.pos_code}${consent.pos_description ? ' — ' + consent.pos_description : ''}`);
        if (consent.modality) parts.push(`Modality: ${consent.modality.replace(/_/g, ' ')}`);
        const displayStr = parts.length > 0 ? parts.join(' | ') : 'Confirmed';
        return (
          <div key={idx} className="group relative bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
            <Label className="text-xs font-semibold text-gray-700">{label}</Label>
            <p className="mt-1 text-xs text-gray-800">{displayStr}</p>
            <FieldActions fieldId={fieldId} value={consent} label={label} displayText={displayStr} />
          </div>
        );
      }
      return (
        <div key={idx} className="bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
          <p className="mt-1 text-xs text-gray-400 italic">(not confirmed)</p>
        </div>
      );
    }

    // Treatment plan goals — pull from note-level linked_goals
    if (ftype === 'treatment_plan_goals') {
      const goals = referenceNoteData?.linked_goals || (Array.isArray(value) ? value : []);
      return (
        <div key={idx} className="bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
          <p className="mt-1 text-xs text-gray-600">
            {goals.length > 0 ? `${goals.length} goal(s) linked` : '(no goals linked)'}
          </p>
        </div>
      );
    }

    if (ftype === 'matrix' || ftype === 'signature') {
      return (
        <div key={idx} className="bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
          <p className="mt-1 text-xs text-gray-400 italic">{ftype === 'matrix' ? '(matrix/grid)' : value ? '(signed)' : '(not signed)'}</p>
        </div>
      );
    }

    // Client diagnoses selector — resolve IDs to readable names
    if (ftype === 'client_diagnoses_selector' || ftype === 'client_diagnoses') {
      const resolved = resolveDiagnosisValue(Array.isArray(value) ? value : []);
      const isEmpty = !resolved || resolved.length === 0;
      return (
        <div key={idx} className="group relative bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
          {isEmpty ? (
            <p className="mt-1 text-xs text-gray-400 italic">(no diagnoses selected)</p>
          ) : (
            <div className="mt-1.5 space-y-1">
              {resolved.map((dx, di) => (
                <div key={di} className="flex items-center space-x-2 px-2 py-1 rounded bg-teal-50 border border-teal-200">
                  <div className="flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center border-teal-500 bg-teal-500">
                    <span className="text-white text-[9px]">&#10003;</span>
                  </div>
                  <span className="text-xs text-teal-800 font-medium">{dx}</span>
                </div>
              ))}
            </div>
          )}
          {!isEmpty && <FieldActions fieldId={fieldId} value={value} label={label} displayText={resolved.join(', ')} />}
        </div>
      );
    }

    // Convert value safely for display — never render raw objects
    let displayValue = '';
    if (Array.isArray(value)) {
      displayValue = value.map(v => {
        if (typeof v === 'object' && v !== null) {
          return v.label || v.value || v.description || v.diagnosis_description || v.code || v.diagnosis_code || v.name || JSON.stringify(v);
        }
        return String(v);
      }).join(', ');
    } else if (typeof value === 'object' && value !== null) {
      displayValue = Object.entries(value)
        .filter(([, v]) => v && v !== '' && v !== null)
        .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join(', ') || '(complex data)';
    } else {
      displayValue = String(value || '');
    }
    const isEmpty = !displayValue.trim();

    // Checkbox group / multiselect
    if (ftype === 'checkbox_group' || ftype === 'multiselect' || ftype === 'multi-select') {
      const selectedValues = Array.isArray(value) ? value.map(v => typeof v === 'object' ? (v.value || v.label || v.id || JSON.stringify(v)) : v) : [];
      return (
        <div key={idx} className="group relative bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
          <Label className="text-xs font-semibold text-gray-700">{label}</Label>
          <div className="mt-1.5 grid gap-1">
            {(field.options || []).map((option, oi) => {
              const optLabel = getOptionLabel(option);
              const optValue = typeof option === 'object' ? (option.value || option.label) : option;
              const isSelected = selectedValues.includes(optValue) || selectedValues.includes(optLabel);
              return (
                <div key={oi} className={`flex items-center space-x-2 px-2 py-1 rounded ${isSelected ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-100'}`}>
                  <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${isSelected ? 'border-teal-500 bg-teal-500' : 'border-gray-300 bg-white'}`}>
                    {isSelected && <span className="text-white text-[9px]">&#10003;</span>}
                  </div>
                  <span className={`text-xs ${isSelected ? 'text-teal-800 font-medium' : 'text-gray-500'}`}>{optLabel}</span>
                </div>
              );
            })}
          </div>
          {selectedValues.length > 0 && <FieldActions fieldId={fieldId} value={selectedValues} label={label} />}
        </div>
      );
    }

    // Radio / select / dropdown
    if (ftype === 'radio' || ftype === 'select' || ftype === 'dropdown') {
      if (field.options && field.options.length > 0) {
        return (
          <div key={idx} className="group relative bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
            <Label className="text-xs font-semibold text-gray-700">{label}</Label>
            <div className="mt-1.5 grid gap-1">
              {field.options.map((option, oi) => {
                const optLabel = getOptionLabel(option);
                const optValue = typeof option === 'object' ? (option.value || option.label) : option;
                const isSelected = value === optValue || value === optLabel;
                return (
                  <div key={oi} className={`flex items-center space-x-2 px-2 py-1 rounded ${isSelected ? 'bg-teal-50 border border-teal-200' : 'bg-gray-50 border border-gray-100'}`}>
                    <div className={`flex-shrink-0 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-teal-500 bg-teal-500' : 'border-gray-300 bg-white'}`}>
                      {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <span className={`text-xs ${isSelected ? 'text-teal-800 font-medium' : 'text-gray-500'}`}>{optLabel}</span>
                  </div>
                );
              })}
            </div>
            {!isEmpty && <FieldActions fieldId={fieldId} value={value} label={label} />}
          </div>
        );
      }
    }

    // Single checkbox
    if (ftype === 'checkbox') {
      const checked = value === true || value === 'true' || value === 'yes' || value === 'Yes';
      return (
        <div key={idx} className="group relative bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
          <div className="flex items-center space-x-2">
            <div className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${checked ? 'border-teal-500 bg-teal-500' : 'border-gray-300 bg-white'}`}>
              {checked && <span className="text-white text-[9px]">&#10003;</span>}
            </div>
            <span className={`text-xs ${checked ? 'text-teal-800 font-medium' : 'text-gray-600'}`}>{label}</span>
          </div>
          <FieldActions fieldId={fieldId} value={value} label={label} displayText={checked ? 'Yes' : 'No'} />
        </div>
      );
    }

    // Default: text, textarea, date, time, number — Copy + Apply
    return (
      <div key={idx} className="group relative bg-white border rounded-lg p-2.5 shadow-sm mb-1.5">
        <Label className="text-xs font-semibold text-gray-700">{label}</Label>
        <p className={`mt-1 text-xs leading-relaxed whitespace-pre-wrap ${isEmpty ? 'text-gray-400 italic' : 'text-gray-800'}`}>
          {isEmpty ? '(not filled)' : displayValue}
        </p>
        {!isEmpty && <FieldActions fieldId={fieldId} value={value} label={label} displayText={displayValue} />}
      </div>
    );
  };

  // Apply ALL non-structural fields at once
  const applyAll = () => {
    if (!referenceNoteData) return;
    try {
      const refData = typeof referenceNoteData.content === 'string'
        ? JSON.parse(referenceNoteData.content) : referenceNoteData.content;
      const refFields = refData?.fields || [];
      const allFields = refFields.length > 0 ? refFields :
        (refData?.sections || []).flatMap(s => s.fields || []);
      
      const fieldsToApply = [];
      allFields.forEach(f => {
        if (!f.id || !f.label) return;
        // For consent_affirmation, use note-level telehealth_consent data
        if (f.type === 'consent_affirmation') {
          const consent = referenceNoteData?.telehealth_consent;
          if (consent && typeof consent === 'object' && Object.keys(consent).length > 0) {
            fieldsToApply.push({ id: f.id, value: consent, label: f.label, type: 'consent_affirmation' });
          }
          return;
        }
        if (isStructuralField(f.type)) return;
        const val = f.value ?? f.default_value;
        if (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0)) return;
        fieldsToApply.push({ id: f.id, value: val, label: f.label });
      });

      if (fieldsToApply.length === 0) {
        toast.info('No filled fields to apply');
        return;
      }

      if (onApplyAll) {
        onApplyAll(fieldsToApply);
      } else if (onApplyField) {
        fieldsToApply.forEach(f => onApplyField(f.id, f.value, f.label));
      }
      toast.success(`Applied ${fieldsToApply.length} fields to current note`);
    } catch (e) {
      console.error('ApplyAll error:', e);
      toast.error('Failed to apply fields');
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>;
    }

    if (referenceNoteData) {
      try {
        const refData = typeof referenceNoteData.content === 'string'
          ? JSON.parse(referenceNoteData.content)
          : referenceNoteData.content;
        const refFields = refData?.fields || [];
        const refSections = refData?.sections || [];
        const refProvider = users.find(u => u.id === referenceNoteData.provider_id);

        const fieldsToRender = refFields.length > 0 ? refFields : 
          refSections.flatMap(s => [
            { type: 'section_header', label: s.title },
            ...(s.fields || [])
          ]);

        return (
          <div className="space-y-0">
            <div className="text-xs text-gray-500 mb-3 pb-2 border-b">
              <p><strong>DOS:</strong> {formatLocalDate(referenceNoteData.date_of_service)}</p>
              <p><strong>Provider:</strong> {refProvider?.full_name || 'N/A'}</p>
              <p><strong>Status:</strong> {referenceNoteData.status?.replace(/_/g, ' ')}</p>
            </div>
            {/* Apply All + Legend */}
            {onApplyField && (
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] text-gray-400 flex items-center gap-3">
                  <span className="flex items-center gap-1"><Copy className="h-2.5 w-2.5" /> Copy</span>
                  <span className="flex items-center gap-1"><ClipboardPaste className="h-2.5 w-2.5 text-teal-500" /> Apply</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-3 text-xs border-teal-300 text-teal-700 hover:bg-teal-50"
                  onClick={applyAll}
                  data-testid="reference-apply-all"
                >
                  <CopyCheck className="h-3.5 w-3.5 mr-1.5" />
                  Apply All
                </Button>
              </div>
            )}
            {fieldsToRender.length > 0 ? (
              fieldsToRender.map(renderField)
            ) : (
              <p className="text-sm text-gray-400 italic">No fields found in this note.</p>
            )}
          </div>
        );
      } catch (e) {
        console.error('Error parsing reference note:', e);
        return <p className="text-sm text-gray-500">Could not parse reference note content.</p>;
      }
    }

    return (
      <div className="text-center py-12 text-gray-400">
        <Columns2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">Select a note above to view it side-by-side</p>
        <p className="text-xs mt-1">Hover over fields to copy or apply them</p>
      </div>
    );
  };

  return ReactDOM.createPortal(
    <div className="flex flex-col bg-white overflow-hidden shadow-2xl border-l" style={{ position: 'fixed', top: '2.5vh', right: '2.5vw', bottom: '2.5vh', width: '40vw', zIndex: 9999, pointerEvents: 'auto', borderTopRightRadius: '12px', borderBottomRightRadius: '12px' }} data-testid="reference-note-panel">
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center justify-between flex-shrink-0">
        <span className="font-semibold text-sm text-gray-700">Reference Note</span>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={(e) => { e.stopPropagation(); onClose(); }}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-4 py-3 border-b bg-white flex-shrink-0">
        <div className="flex items-center justify-between mb-1">
          <Label className="text-xs text-gray-500">Select a note from this client</Label>
          <button
            className={`text-xs px-2 py-0.5 rounded-full transition-colors ${showAllNotes ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            onClick={() => setShowAllNotes(!showAllNotes)}
            data-testid="reference-show-all-toggle"
          >
            {loadingAllNotes ? 'Loading...' : showAllNotes ? 'Recent only' : 'Show all'}
          </button>
        </div>
        <select
          value={referenceNoteData?.id || ''}
          onChange={(e) => loadNote(e.target.value)}
          className="w-full text-sm h-9 rounded-md border border-input bg-background px-3 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          data-testid="reference-note-select"
        >
          <option value="" disabled>Choose a note to compare...</option>
          {displayNotes.map(n => {
            const noteTemplate = formTemplates.find(t => t.id === n.template_id);
            const dosDate = formatLocalDate(n.date_of_service);
            return (
              <option key={n.id} value={n.id}>
                {dosDate} — {noteTemplate?.name || n.template_name || n.title || 'Note'} ({n.status?.replace(/_/g, ' ')})
              </option>
            );
          })}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3"
        onWheel={(e) => {
          const el = e.currentTarget;
          el.scrollTop += e.deltaY;
          e.stopPropagation();
        }}
      >
        {renderContent()}
      </div>
    </div>,
    document.body
  );
};

export default ReferenceNotePanel;
